import React, { useEffect, useState } from 'react';
import MemberLayout from '../../components/MemberLayout';
import { getMyDocuments, uploadDocument, downloadMyDocument } from '../../services/api';

// File types a browser can reasonably display inline in a new tab.
// Anything else (docx, etc.) will just trigger a download either way,
// since the browser has no native way to render it.
const VIEWABLE_TYPES = ['image/', 'application/pdf'];

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const loadDocuments = () => {
    getMyDocuments().then((res) => setDocuments(res.data)).catch(() => {});
  };

  useEffect(() => { loadDocuments(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadDocument(formData);
      setFile(null);
      loadDocuments();
    } catch (err) {
      setError('Upload failed. Please try a smaller file (max 10MB).');
    } finally {
      setUploading(false);
    }
  };

  const isViewable = (fileType) => VIEWABLE_TYPES.some((t) => (fileType || '').startsWith(t));

  const handleView = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await downloadMyDocument(doc.id);
      const blob = new Blob([res.data], { type: doc.fileType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Give the new tab a moment to load before revoking
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      alert('Could not open this file.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await downloadMyDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download this file.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <MemberLayout title="Documents">
      <form onSubmit={handleUpload} className="card" style={{ marginBottom: 24, maxWidth: 520 }}>
        <div className="form-group">
          <label>Upload a Document</label>
          <input type="file" onChange={(e) => setFile(e.target.files[0])} />
          <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', marginTop: 6 }}>
            Employment contracts, payslips, warning letters, dismissal letters, correspondence, photos, videos or voice notes.
          </p>
        </div>
        {error && <p className="error-text">{error}</p>}
        <button className="btn btn-gold" disabled={!file || uploading}>{uploading ? 'Uploading...' : 'Upload'}</button>
      </form>

      {documents.length === 0 ? (
        <p>No documents uploaded yet.</p>
      ) : (
        <div className="card">
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {documents.map((d) => (
              <li key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                <div>
                  <div>{d.fileName}</div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-grey)' }}>
                    {d.uploadedBy === 'MEMBER' ? 'Uploaded by you' : 'Uploaded by organisation'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isViewable(d.fileType) && (
                    <button
                      className="btn btn-outline-dark"
                      style={{ padding: '6px 14px' }}
                      onClick={() => handleView(d)}
                      disabled={busyId === d.id}
                    >
                      View
                    </button>
                  )}
                  <button
                    className="btn btn-gold"
                    style={{ padding: '6px 14px' }}
                    onClick={() => handleDownload(d)}
                    disabled={busyId === d.id}
                  >
                    {busyId === d.id ? '...' : 'Download'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </MemberLayout>
  );
}
